import React, { useEffect, useMemo, useRef } from 'react';
import { FormFooter } from './FormFooter';
import type { MultiStepFormContainerProps, StepContentProps } from './types';
import { StepIndicator } from './StepIndicator';
import { cn } from '@rocco/utils/cn';

/**
 * Pure UI multi-step form container component
 * All business logic is handled by the parent component
 */
export const MultiStepFormContainer: React.FC<MultiStepFormContainerProps> = ({
    stepConfigs,
    currentStepIndex,
    totalSteps,
    currentVisualDotIndex,
    totalVisualDots,
    isStepValid,
    isLoading,
    errors,
    className = '',
    nextButtonText = 'Next',
    nextButtonClassName,
    backButtonText = 'Back',
    cancelButtonText = 'Cancel',
    skipButtonText = 'Skip this step',
    secondaryActionText,
    showSecondaryAction = false,
    hideNextButton,
    forceShowBackButton,
    hideCancelButton,
    hideStepIndicator,
    stepIndicatorLabel,
    stepIndicatorClassName,
    stepIndicatorVariant,
    hideFooter,
    variant = 'default',
    initialData,
    onNext,
    onBack,
    onCancel,
    onSkip,
    onSecondaryAction,
    onStepDataChange,
    setIsValid,
}) => {
    // Computed values based on props
    const currentStep = useMemo(
        () => stepConfigs[currentStepIndex],
        [stepConfigs, currentStepIndex],
    );

    const isFirstStep = useMemo(
        () => currentStepIndex === 0,
        [currentStepIndex],
    );

    const isLastStep = useMemo(
        () => currentStepIndex === totalSteps - 1,
        [currentStepIndex, totalSteps],
    );

    const scrollRef = useRef<HTMLDivElement>(null);

    // Scroll to top when step changes
    useEffect(() => {
        scrollRef.current?.scrollTo({ top: 0, behavior: 'auto' });
    }, [currentStepIndex]);

    // Don't render if no current step
    if (!currentStep) {
        console.error(`No step found at index ${currentStepIndex}`);
        return null;
    }

    // Prepare props for the current step component
    const stepContentProps: StepContentProps = {
        stepConfig: currentStep,
        currentStepIndex,
        totalSteps,
        isFirstStep,
        isLastStep,
        onNext,
        onBack: isFirstStep ? () => {} : onBack,
        onSkip: currentStep.skippable ? onSkip : () => {},
        goToStep: () => {}, // Not implemented in pure UI version
        setIsValid,
        onDataChange: onStepDataChange,
        initialData,
    };

    // Get the component for the current step
    const StepComponent = currentStep.component;

    // Determine next button text based on step and loading state
    let finalNextButtonText = nextButtonText;
    if (isLastStep) {
        finalNextButtonText = isLoading ? 'Submitting...' : 'Complete';
    } else if (isLoading) {
        finalNextButtonText = 'Loading...';
    }

    return (
        <div
            className={cn(
                'multi-step-form',
                'relative flex min-h-0 flex-col overflow-hidden',
                variant === 'hde'
                    ? 'w-full flex-1 bg-[#EAEAEA] text-gray-900'
                    : 'mx-auto h-[48rem] w-full max-w-[33.75rem] rounded-xl bg-white shadow-md',
                className,
            )}
            role="form"
            aria-label="Multi-step form"
        >
            <div
                ref={scrollRef}
                className={cn(
                    'flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden',
                    variant === 'hde' ? 'scrollbar-elegant' : 'scrollbar-hide',
                    variant === 'hde'
                        ? cn(
                              'flex-col gap-4.5 px-6 pt-6 md:gap-4.5 md:px-10 md:pt-12',
                              hideStepIndicator ? 'md:px-13' : 'md:px-18',
                          )
                        : 'px-9',
                    !hideFooter && variant === 'hde' && 'pb-20 md:pb-28',
                )}
            >
                {!hideStepIndicator && (
                    <StepIndicator
                        currentStepIndex={currentStepIndex}
                        totalSteps={totalSteps}
                        currentVisualDotIndex={currentVisualDotIndex}
                        totalVisualDots={totalVisualDots}
                        variant={
                            stepIndicatorVariant ??
                            (variant === 'hde' ? 'bar' : 'dots')
                        }
                        label={stepIndicatorLabel}
                        className={stepIndicatorClassName}
                    />
                )}

                {errors.general && (
                    <div className="mx-9 mb-4 rounded-lg bg-red-50 p-3 text-red-700">
                        {errors.general}
                    </div>
                )}

                <StepComponent {...stepContentProps} />
            </div>

            {!hideFooter && (
                <div
                    className={cn(
                        'absolute bottom-0 left-0 right-0 z-10 shrink-0 pb-10 pt-4 md:pb-20 md:pt-0',
                        variant === 'hde' && 'bg-[#EAEAEA]',
                        variant !== 'hde' && 'bg-white',
                    )}
                >
                    <FormFooter
                        isNextDisabled={!isStepValid || isLoading}
                        hideNextButton={
                            hideNextButton !== undefined
                                ? hideNextButton
                                : currentStep.hideNextButton
                        }
                        showBackButton={Boolean(
                            forceShowBackButton ||
                            (!isFirstStep &&
                                currentStep.allowGoingBack !== false) ||
                            (isFirstStep &&
                                !hideCancelButton &&
                                (variant === 'hde' ||
                                    currentStep.showBackInsteadOfCancel)),
                        )}
                        showBackInsteadOfCancel={Boolean(
                            isFirstStep &&
                            !hideCancelButton &&
                            (variant === 'hde' ||
                                currentStep.showBackInsteadOfCancel),
                        )}
                        hideCancelButton={hideCancelButton}
                        showSkipButton={currentStep.skippable}
                        nextButtonText={finalNextButtonText}
                        backButtonText={backButtonText}
                        cancelButtonText={cancelButtonText}
                        skipButtonText={skipButtonText}
                        secondaryActionText={secondaryActionText}
                        showSecondaryAction={showSecondaryAction}
                        isFirstStep={isFirstStep}
                        isLastStep={isLastStep}
                        onNext={onNext}
                        onBack={onBack}
                        onCancel={onCancel}
                        onSkip={onSkip}
                        onSecondaryAction={onSecondaryAction}
                        variant={variant}
                        nextButtonClassName={nextButtonClassName}
                    />
                </div>
            )}
        </div>
    );
};
